import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  Check,
  Clock,
  CheckCheck,
  Users,
  UserRound,
  Building2,
  Star,
  Eye,
  EyeOff,
  ArrowDownAZ,
  ArrowDownWideNarrow,
  Filter,
  FilterX,
  ListChecks,
  ChevronDown,
  Hash,
  Loader2,
  MessageSquare,
} from "lucide-react";
import { toast } from "react-toastify";

import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { cn } from "../../lib/utils";

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsList";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useWhatsApps from "../../hooks/useWhatsApps";
import useQueues from "../../hooks/useQueues";
import api from "../../services/api";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import ListaDoChatInterno from "./ListaDoChatInterno";
import toastError from "../../errors/toastError";
import { SEM_FILA } from "../../constants/filas";
import FiltroDeFilasModal from "../FiltroDeFilasModal";

// Botão de ferramenta da lista: só o ícone, com o nome aparecendo embaixo no
// hover — como na referência, onde o rótulo surge quando a mão chega.
// Traço entre grupos de ícones. Sem ele a barra é uma fileira de nove
// símbolos sem hierarquia: o que muda o RECORTE da lista fica igual ao que
// executa uma AÇÃO nela, e a pessoa lê tudo de uma vez ou nada.
const SeparadorDeFerramentas = () => (
  <span aria-hidden className="mx-1 h-5 w-px shrink-0 bg-border" />
);

const FerramentaDaLista = ({
  icone: Icone,
  rotulo,
  ativo,
  onClick,
  classe,
}) => (
  <div className="group/ferramenta relative">
    <button
      type="button"
      aria-label={rotulo}
      aria-pressed={ativo}
      onClick={onClick}
      className={cn(
        "flex h-8 w-8 items-center justify-center rounded-full transition-colors",
        ativo
          ? "bg-primary/10 text-primary"
          : cn(
              "text-muted-foreground hover:bg-muted hover:text-foreground",
              classe,
            ),
      )}
    >
      <Icone className="h-[18px] w-[18px]" />
    </button>

    <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-0.5 -translate-x-1/2 whitespace-nowrap rounded bg-foreground px-1.5 py-0.5 text-[10px] font-medium text-background opacity-0 transition-opacity group-hover/ferramenta:opacity-100">
      {rotulo}
    </span>
  </div>
);

// Filtro de várias escolhas. Encapsula o padrão "rótulo + quantos escolhidos
// + lista com caixas de seleção", que se repete nos três filtros.
const FiltroDeLista = ({ rotulo, opcoes, selecionados, onChange }) => {
  const alternar = (id) =>
    onChange(
      selecionados.includes(id)
        ? selecionados.filter((s) => s !== id)
        : [...selecionados, id],
    );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-8 w-full items-center gap-1.5 rounded-md border px-2.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          {rotulo}
          {selecionados.length > 0 && (
            <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold tabular-nums text-primary-foreground">
              {selecionados.length}
            </span>
          )}
          <ChevronDown className="ml-auto h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="scrollbar-discreta max-h-72 w-64 overflow-y-auto p-1"
      >
        {(opcoes || []).length === 0 ? (
          <p className="p-3 text-xs text-muted-foreground">
            Nada para filtrar.
          </p>
        ) : (
          opcoes.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => alternar(o.id)}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs hover:bg-muted"
            >
              <span
                className={cn(
                  "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                  selecionados.includes(o.id)
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input",
                )}
              >
                {selecionados.includes(o.id) && <Check className="h-3 w-3" />}
              </span>
              {o.color && (
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: o.color }}
                />
              )}
              <span className="truncate">{o.name}</span>
            </button>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
};

// As abas deixaram de ser por SITUAÇÃO (open/pending) e passaram a ser por
// DONO: "é meu", "é do meu setor", "não é de ninguém ainda". É a pergunta que
// quem atende realmente faz ao abrir a lista — "Atendendo" respondia
// "alguém está atendendo", que não ajuda a decidir o que fazer agora.
//
// Resolvidos é uma aba como as outras. Antes era um painel à parte, atrás de
// um ícone pequeno na barra de ferramentas: quem não conhecia a tela não
// achava o histórico, e quem conhecia precisava de dois cliques em lugares
// diferentes para ir de "Aguardando" a "Resolvidos". Grupos segue como aba
// própria porque grupo não tem dono nem entra no eixo meu/do setor — ficaria
// eternamente em "Aguardando", empurrando conversa de gente de verdade para
// baixo.
const ESTADOS = [
  {
    value: "minhas",
    rotulo: "Atendimento",
    icone: UserRound,
    // O recorte (só os meus, ou os do setor inteiro) deixou de ser uma aba e
    // virou o botão de prédio na barra de cima: eram quatro abas disputando a
    // largura da coluna, e "Do setor" é um filtro do que já está em
    // atendimento, não um lugar diferente.
    visao: "minhas",
    status: "open",
    ajuda: "Atendimentos em andamento",
    vazio: {
      titulo: "Nada na sua mão",
      texto: "Aceite uma conversa em Aguardando para começar.",
    },
  },
  {
    value: "pending",
    rotulo: "Aguardando",
    icone: Clock,
    status: "pending",
    ajuda: "Ninguém assumiu ainda",
    vazio: {
      titulo: "Nenhum cliente esperando",
      texto: "Novas conversas aparecem aqui assim que chegam.",
    },
  },
  {
    value: "grupos",
    rotulo: "Grupos",
    icone: Users,
    // Grupo não é "aceito" por ninguém: fica em aguardando para sempre. A
    // aba lista as duas situações, senão nunca mostra nada.
    status: "open,pending",
    ajuda: "Conversas em grupos do WhatsApp",
    somenteGrupos: true,
    vazio: {
      titulo: "Nenhuma conversa em grupo",
      texto: "Grupos aparecem aqui quando alguém escreve neles.",
    },
  },
  {
    // Chat da equipe na mesma coluna do atendimento: o recado do colega e a
    // fila do cliente são olhados no mesmo lugar. A conversa em si continua
    // na tela do chat interno — aqui é a porta de entrada.
    value: "interno",
    rotulo: "Interno",
    icone: MessageSquare,
    ajuda: "Conversas com a equipe",
    chatInterno: true,
  },
];

const RESOLVIDOS = {
  value: "closed",
  rotulo: "Resolvidos",
  icone: CheckCheck,
  status: "closed",
  ajuda: "Atendimentos encerrados",
  vazio: {
    titulo: "Nada resolvido por aqui",
    texto: "Os atendimentos encerrados ficam guardados nesta aba.",
  },
};

const TicketsManager = () => {
  const navigate = useNavigate();
  const [searchParam, setSearchParam] = useState("");
  // Com a busca sempre na tela, "estar buscando" é ter texto digitado —
  // não um modo que se liga e desliga.
  const buscando = searchParam.trim().length > 0;
  // Abre em "Minhas": o primeiro olhar da pessoa é para o que é dela.
  const [estado, setEstado] = useState("minhas");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  // Filtro pessoal: mostra só o que a pessoa marcou com a estrela. Vale
  // dentro da aba em que ela está, não substitui a aba.
  const [soFavoritas, setSoFavoritas] = useState(false);
  // Período da aba "Resolvidos". Sem recorte ela cresce para sempre, e o que
  // interessa — o que fechou hoje — some no meio de meses de histórico.
  // Uma semana cobre o uso real: conferir o que a equipe encerrou.
  const [periodoResolvidos, setPeriodoResolvidos] = useState("7");
  const searchInputRef = useRef();
  const { user } = useContext(AuthContext);

  const [contagens, setContagens] = useState({
    minhas: 0,
    setor: 0,
    pending: 0,
    grupos: 0,
  });
  const { whatsApps } = useWhatsApps();

  // A aba de grupos só existe se alguma conexão estiver configurada para
  // atendê-los — do contrário é uma aba sempre vazia.
  const mostrarGrupos = (whatsApps || []).some((w) => w.showGroups);
  // As quatro abas são fixas: Atendimento, Aguardando, Grupos e Interno.
  //
  // Antes a de Grupos sumia quando nenhuma conexão tratava grupo como
  // atendimento — e quem procurava a aba achava que o sistema não tinha
  // grupos. Ela fica no lugar, e o estado vazio diz o que ligar.
  // Resolvidos entra no fim: é histórico, não fila de trabalho.
  const abas = [...ESTADOS, RESOLVIDOS];

  const userQueueIds = user.queues.map((q) => q.id);
  // "Sem fila" entra marcado por padrão: a lista abre mostrando tudo, como
  // sempre mostrou, e quem quiser esconder o que ainda não foi encaminhado
  // desmarca. O contrário — abrir sem ele — faria sumir da tela justamente a
  // conversa que acabou de chegar e ninguém pegou.
  // Vazio para quem não tem fila própria (o administrador, quase sempre): é
  // o efeito abaixo que carrega as filas da empresa, e ele usa "lista vazia"
  // como sinal de que a pessoa ainda não mexeu no funil. Começar com
  // [SEM_FILA] fazia o efeito achar que já tinha escolha feita e a tela abria
  // filtrando só o que está sem fila.
  const [selectedQueueIds, setSelectedQueueIds] = useState(
    userQueueIds?.length ? [...userQueueIds, SEM_FILA] : [],
  );

  // Filas que este usuário pode filtrar.
  //
  // Quem não tem fila vinculada — o administrador, quase sempre — ficava com
  // a seleção vazia, e a lista traduzia isso em "só ticket SEM fila": todo
  // atendimento que ganhava fila sumia da tela, e só voltava ligando o olho
  // do "mostrar todos". O grupo com fila fixa e o ticket recém-aceito
  // desapareciam pelo mesmo motivo.
  //
  // Sem fila própria, e podendo ver a empresa inteira, "nenhuma" passa a
  // significar "todas": as filas da empresa entram como opção do funil e
  // como seleção inicial. Quem é de fila (escopo "queues"/"team"/"own")
  // continua exatamente como antes.
  const { findAll: listarFilas } = useQueues();
  const [filasDaEmpresa, setFilasDaEmpresa] = useState(null);
  // Basta não ter fila própria — o escopo do cargo não entra na conta.
  //
  // A condição exigia `ticketScope === "company"`, e isso deixava de fora
  // justamente quem mais precisa: usuário SEM cargo cai em "own"
  // (`PermissionServices`: `user.ticketScope || role?.ticketScope || "own"`) e,
  // sem fila vinculada, ficava com a lista de opções VAZIA — o filtro abria
  // mostrando só "Sem fila", sem nenhuma fila para marcar. Pior: a seleção
  // nascia vazia, e lista vazia significa "não filtre" no backend, então a
  // tela mostrava tudo e não havia como estreitar. Os dois sintomas
  // relatados — "não filtra" e "não mostra todas as filas" — eram esta linha.
  //
  // Oferecer as filas da empresa não amplia o que ninguém enxerga: o recorte
  // de quem é dono continua sendo do backend, e filtrar por uma fila em que a
  // pessoa não tem nada devolve zero, como deve.
  const semFilaPropria = userQueueIds.length === 0;

  useEffect(() => {
    if (!semFilaPropria || filasDaEmpresa) return;

    let vivo = true;

    listarFilas()
      .then((filas) => {
        if (!vivo) return;
        const lista = Array.isArray(filas) ? filas : [];
        setFilasDaEmpresa(lista);
        // Só define a seleção inicial se o usuário ainda não mexeu no funil.
        setSelectedQueueIds((atual) =>
          atual.length ? atual : [...lista.map((f) => f.id), SEM_FILA],
        );
      })
      .catch(() => setFilasDaEmpresa([]));

    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [semFilaPropria]);

  const filasDoFiltro = semFilaPropria
    ? filasDaEmpresa || []
    : user?.queues || [];

  // "Todos" começa desligado para todo mundo, inclusive administrador: a
  // lista abre com os atendimentos de quem entrou, e ver os dos outros é uma
  // escolha de quem clica no botão. Antes o admin caía direto na fila
  // inteira da empresa e não tinha como enxergar só o que era dele.

  // "abertos" mostra as abas Atendendo/Aguardando/Grupos; "fechados" mostra
  // só os resolvidos, como na referência.
  // O painel deixou de ser escolha própria: ele é a aba. Eram dois controles
  // para o mesmo assunto, e dava para ficar no painel de fechados com a aba
  // "Aguardando" acesa.
  const painel = estado === "closed" ? "fechados" : "abertos";
  const [ordemAZ, setOrdemAZ] = useState(false);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [filtroTags, setFiltroTags] = useState([]);
  const [filtroUsuarios, setFiltroUsuarios] = useState([]);
  const [filtroConexoes, setFiltroConexoes] = useState([]);
  const [etiquetas, setEtiquetas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  // Período (dd/mm/aaaa) e o atalho para ir direto a um atendimento pelo
  // número — é assim que a equipe se refere a ele no dia a dia.
  const [filtroDataInicio, setFiltroDataInicio] = useState("");
  const [filtroDataFim, setFiltroDataFim] = useState("");
  const [numeroDoTicket, setNumeroDoTicket] = useState("");
  // Agrupar a aba de encerrados por cliente. Fica na escolha de quem usa:
  // quem precisa auditar chamado por chamado desliga e vê todos.
  // Não lidas do chat da equipe, para o número na aba Interno.
  const [naoLidasInternas, setNaoLidasInternas] = useState(0);
  // "Do setor": mostra na aba Atendimento o que está com colegas das minhas
  // filas. Lembrado entre sessões — é preferência de como a pessoa trabalha.
  const [filtrandoFilas, setFiltrandoFilas] = useState(false);
  const [verSetor, setVerSetor] = useLocalStorage("verDoSetor", false);
  const [agruparEncerrados, setAgruparEncerrados] = useLocalStorage(
    "agruparEncerrados",
    true,
  );
  const [indoParaTicket, setIndoParaTicket] = useState(false);

  // As opções dos filtros só são buscadas quando os filtros aparecem: quem
  // nunca abre a gaveta não paga por ela.
  useEffect(() => {
    if (!filtrosAbertos || etiquetas.length || usuarios.length) return;

    (async () => {
      try {
        const [t, u] = await Promise.all([api.get("/tags"), api.get("/users")]);
        setEtiquetas(t.data?.tags || t.data || []);
        setUsuarios(u.data?.users || []);
      } catch (err) {
        // Sem as listas os filtros abrem vazios; não vale derrubar a tela.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrosAbertos]);
  const temFiltro =
    filtroTags.length > 0 ||
    filtroUsuarios.length > 0 ||
    filtroConexoes.length > 0 ||
    Boolean(filtroDataInicio) ||
    Boolean(filtroDataFim);

  const limparFiltros = () => {
    setFiltroTags([]);
    setFiltroUsuarios([]);
    setFiltroConexoes([]);
    setFiltroDataInicio("");
    setFiltroDataFim("");
    setNumeroDoTicket("");
  };

  // Confere antes de navegar: mandar direto para /tickets/<n> de um número
  // que não existe (ou de outra empresa) larga a pessoa numa tela de erro.
  const irParaTicket = async () => {
    // Resolve o numero pelo backend: acha tanto o id de ticket novo quanto o
    // numero antigo (que vive no texto das faixas de encerramento importadas).
    const entrada = String(numeroDoTicket).trim();

    if (!entrada) return;

    setIndoParaTicket(true);

    try {
      const { data } = await api.get(
        `/tickets/por-numero/${encodeURIComponent(entrada)}`,
      );
      setNumeroDoTicket("");
      navigate(`/tickets/${data.ticketId}`);
    } catch {
      toast.error(`Atendimento #${entrada} não encontrado.`);
    }

    setIndoParaTicket(false);
  };

  // `previaFecharTodos` guarda o que o servidor diz que seria encerrado: quantos
  // são meus e quantos são de outros atendentes. É null com o diálogo fechado.
  const [previaFecharTodos, setPrevia] = useState(null);

  const abrirFecharTodos = async () => {
    try {
      const { data } = await api.get("/tickets/close-all/preview");
      setPrevia(data);
    } catch (err) {
      toastError(err);
    }
  };

  // O padrão é encerrar só o que é meu. Os de outros atendentes só entram
  // quando a pessoa pede, já tendo lido de quem são: uma vez as conversas de um
  // colega sumiram de uma vez, sem aviso, e ninguém soube explicar.
  const fecharTodos = async (incluirDeOutros) => {
    setPrevia(null);

    try {
      const { data } = await api.put("/tickets/close-all", { incluirDeOutros });
      toast.success(
        data?.encerrados
          ? `${data.encerrados} atendimento(s) encerrado(s).`
          : "Nenhum atendimento em andamento.",
      );
    } catch (err) {
      toastError(err);
    }
  };

  // O timer fica num ref, não numa variável do corpo do componente: aquela
  // nascia de novo a cada render, então o clearTimeout limpava `undefined` e
  // a busca não era adiada — cada tecla disparava uma consulta de tickets.
  const searchTimeout = useRef();

  const handleSearch = (e) => {
    const termo = e.target.value.toLowerCase();
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setSearchParam(termo), 500);
  };

  // Digitar e sair da tela deixaria o timer pendente escrevendo estado em
  // componente desmontado.
  useEffect(() => () => clearTimeout(searchTimeout.current), []);

  const atualizarContagem = (chave) => (valor) =>
    setContagens((anterior) =>
      anterior[chave] === valor ? anterior : { ...anterior, [chave]: valor },
    );

  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-background">
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={() => setNewTicketModalOpen(false)}
      />

      {/* Busca, com o interruptor que amplia a procura para o conteúdo das
          mensagens — é mais lenta, então fica desligada por padrão. */}
      <div className="flex shrink-0 items-center gap-2 px-3 pb-1.5 pt-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            placeholder="Buscar atendimento e mensagens"
            type="search"
            onChange={handleSearch}
            className="h-9 rounded-full pl-9"
          />
        </div>

        <button
          type="button"
          aria-label={filtrosAbertos ? "Esconder filtros" : "Mostrar filtros"}
          title={filtrosAbertos ? "Esconder filtros" : "Mostrar filtros"}
          aria-pressed={filtrosAbertos}
          onClick={() => setFiltrosAbertos((v) => !v)}
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors",
            filtrosAbertos
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          {filtrosAbertos ? (
            <Filter className="h-4 w-4" />
          ) : (
            <FilterX className="h-4 w-4" />
          )}
        </button>
      </div>

      {filtrosAbertos && (
        <div className="shrink-0 space-y-2 border-b px-3 pb-3">
          <FiltroDeLista
            rotulo="Etiquetas"
            opcoes={etiquetas}
            selecionados={filtroTags}
            onChange={setFiltroTags}
          />
          <FiltroDeLista
            rotulo="Conexões"
            opcoes={whatsApps}
            selecionados={filtroConexoes}
            onChange={setFiltroConexoes}
          />
          <Can
            role={user.profile}
            perform="tickets-manager:showall"
            yes={() => (
              <FiltroDeLista
                rotulo="Atendentes"
                opcoes={usuarios}
                selecionados={filtroUsuarios}
                onChange={setFiltroUsuarios}
              />
            )}
          />

          {/* Período de abertura. Dois campos, e não um só: "de tal dia até
              tal dia" é como se pede um relatório de atendimento. */}
          <div className="flex items-center gap-2">
            <Input
              type="date"
              aria-label="Abertos a partir de"
              title="Abertos a partir de"
              value={filtroDataInicio}
              onChange={(e) => setFiltroDataInicio(e.target.value)}
              className="h-9 flex-1"
            />
            <Input
              type="date"
              aria-label="Abertos até"
              title="Abertos até"
              value={filtroDataFim}
              onChange={(e) => setFiltroDataFim(e.target.value)}
              className="h-9 flex-1"
            />
          </div>

          {/* Ir direto ao atendimento pelo número. A busca acima também acha
              pelo número, mas quem já sabe qual é quer abrir, não procurar. */}
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              irParaTicket();
            }}
          >
            <div className="relative flex-1">
              <Hash className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={numeroDoTicket}
                onChange={(e) =>
                  setNumeroDoTicket(e.target.value.replace(/[^\d-]/g, ""))
                }
                placeholder="Ir para ticket..."
                inputMode="numeric"
                aria-label="Ir para o atendimento pelo número"
                className="h-9 pl-8"
              />
            </div>
            <Button
              type="submit"
              variant="secondary"
              size="sm"
              className="h-9"
              disabled={!numeroDoTicket || indoParaTicket}
            >
              {indoParaTicket ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Ir"
              )}
            </Button>
          </form>

          {temFiltro && (
            <button
              type="button"
              onClick={limparFiltros}
              className="mx-auto block text-xs font-medium text-primary hover:underline"
            >
              Limpar filtros
            </button>
          )}
        </div>
      )}

      {/* Barra de ferramentas, em três grupos separados por um traço:
          o que muda o RECORTE (todos, do setor), o que é AÇÃO (novo,
          favoritas, resolver todos) e o que troca o PAINEL (abertos,
          fechados, ordem). Antes eram nove ícones em fileira, todos com o
          mesmo peso. */}
      <div className="mx-3 mb-2 flex shrink-0 items-center gap-0.5 rounded-lg border bg-card px-1.5 py-1 shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
        <Can
          role={user.profile}
          perform="tickets-manager:showall"
          yes={() => (
            <FerramentaDaLista
              icone={showAllTickets ? Eye : EyeOff}
              rotulo="Todos"
              ativo={showAllTickets}
              onClick={() => setShowAllTickets((v) => !v)}
            />
          )}
        />

        {/* Do setor: era uma aba e virou filtro. Ligado, a aba Atendimento
            mostra também o que está com colegas das suas filas — é o mesmo
            "em atendimento", visto de mais perto ou de mais longe. */}
        <FerramentaDaLista
          icone={Building2}
          rotulo="Do setor"
          ativo={verSetor}
          onClick={() => setVerSetor((v) => !v)}
        />

        <SeparadorDeFerramentas />

        <FerramentaDaLista
          icone={Plus}
          rotulo="Novo"
          onClick={() => setNewTicketModalOpen(true)}
        />

        <FerramentaDaLista
          icone={Star}
          rotulo="Favoritas"
          ativo={soFavoritas}
          classe="text-amber-500"
          onClick={() => setSoFavoritas((v) => !v)}
        />

        <FerramentaDaLista
          icone={ListChecks}
          rotulo="Resolver todos"
          classe="text-emerald-600 dark:text-emerald-400"
          onClick={abrirFecharTodos}
        />

        <SeparadorDeFerramentas />

        <FerramentaDaLista
          icone={ordemAZ ? ArrowDownAZ : ArrowDownWideNarrow}
          rotulo={ordemAZ ? "Crescente" : "Decrescente"}
          ativo={ordemAZ}
          onClick={() => setOrdemAZ((v) => !v)}
        />

        <div className="ml-auto">
          <button
            type="button"
            onClick={() => setFiltrandoFilas(true)}
            className="flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            Filas
            {selectedQueueIds.length > 0 && (
              <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-muted px-1 text-[10px] font-semibold tabular-nums">
                {selectedQueueIds.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Abas em pílula, como o resto da interface (shadcn Tabs).
          O sublinhado fino não dizia onde uma aba termina e a outra começa —
          quatro palavras enfileiradas pareciam uma frase. Aqui a escolhida
          ganha uma pastilha com fundo próprio e sombra, e o trilho cinza
          delimita cada célula. Só existem no painel de abertos. */}
      <div className="shrink-0 border-b px-2 pb-2">
        <div
          role="tablist"
          aria-label="Atendimento, aguardando, grupos, interno e resolvidos"
          // As cinco abas numa linha só, cada uma do tamanho da própria
          // palavra.
          //
          // Em células de largura igual — que é o que `grid` ou `flex-1`
          // isolado fazem — a fita de 355px dá 71px por aba: "Atendimento"
          // precisa de 66px mais o contador e era cortado, enquanto "Grupos"
          // (37px) desperdiçava metade da célula. Somados, os cinco rótulos dão
          // 256px: o espaço sempre existiu, estava mal repartido.
          //
          // `basis-auto` faz a aba nascer com a largura do texto; o `grow`
          // divide só a sobra. Assim cada nome aparece inteiro e a linha
          // continua preenchida de ponta a ponta.
          className="flex gap-0.5 rounded-lg bg-muted p-1"
        >
          {abas.map((item) => {
            const ativo = estado === item.value;
            // A aba Interno conta as não lidas do chat da equipe; as demais,
            // os atendimentos daquele recorte.
            const contagem = item.chatInterno
              ? naoLidasInternas
              : contagens[item.value];
            return (
              <button
                key={item.value}
                type="button"
                role="tab"
                aria-selected={ativo}
                title={item.ajuda}
                onClick={() => setEstado(item.value)}
                className={cn(
                  // O rótulo precisa caber INTEIRO em quatro abas num celular
                  // de 360px — sobram ~90px por aba, e "ATENDIMENTO" em 11px
                  // maiúsculo com espaçamento não passa. Na tela estreita o
                  // texto perde o espaçamento extra e encolhe um ponto; a
                  // partir de sm volta ao tamanho de antes.
                  //
                  // O contador saiu de junto do ícone (onde precisava de um
                  // vão de 12px para não colar na primeira letra) e foi para o
                  // canto do botão: são 10px de largura recuperados por aba.
                  // Cada aba tem ~91px: a coluna de conversas é estreita e são
                  // quatro. Com ícone e maiúsculas, "ATENDIMENTO" precisava de
                  // 113px e invadia a aba vizinha.
                  //
                  // Sem ícone, em caixa normal e com o contador ao lado, o
                  // rótulo cabe inteiro na própria célula — e é o desenho da
                  // referência: "Atendimento 3 | Aguardando | Grupos | Interno".
                  // `px-0.5`: com `px-1` a aba de rótulo mais longo ("Aguardando"
                  // com contador) passava 2px do próprio espaço e o
                  // `overflow-hidden` comia a borda da bolinha do contador.
                  // `basis-auto` (padrão do flex): largura do conteúdo. O
                  // `grow` reparte a sobra. `px-1` porque o respiro que
                  // importa aqui é o vão entre as abas, não a margem interna.
                  "relative flex min-w-0 grow items-center justify-center gap-1 whitespace-nowrap rounded-md px-1 py-1.5 text-[11px] font-semibold tracking-normal transition-all",
                  ativo
                    ? "bg-background text-foreground shadow-sm ring-1 ring-black/[0.04] dark:ring-white/10"
                    : "text-muted-foreground hover:bg-background/60 hover:text-foreground",
                )}
              >
                {/* Sem `truncate`: cortar "Atendimento" em "Atendim…" não
                    poupa espaço e só esconde a palavra. */}
                <span className="whitespace-nowrap">{item.rotulo}</span>

                {/* O contador ao lado, e não pendurado sobre o ícone: ali ele
                    cobria a primeira letra quando passava de 9. */}
                {/* O contador acompanha o estado da aba: cheio na escolhida,
                    discreto nas outras — senão quatro bolinhas coloridas
                    disputam a atenção e nenhuma diz nada. */}
                {contagem > 0 && (
                  <span
                    className={cn(
                      "flex h-3.5 min-w-3.5 shrink-0 items-center justify-center rounded-full px-0.5 text-[9px] font-bold tabular-nums",
                      ativo
                        ? "bg-primary text-primary-foreground"
                        : "bg-primary/15 text-primary",
                    )}
                  >
                    {/* Teto de dois dígitos: um contador de três dígitos
                        rouba a largura do rótulo vizinho, e "quantos
                        exatamente" nunca foi a pergunta desta bolinha. */}
                    {contagem > 99 ? "99+" : contagem}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <FiltroDeFilasModal
        aberto={filtrandoFilas}
        aoFechar={() => setFiltrandoFilas(false)}
        filas={filasDoFiltro}
        escolhidas={selectedQueueIds}
        aoAplicar={setSelectedQueueIds}
      />

      <Dialog open={!!previaFecharTodos} onOpenChange={() => setPrevia(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Encerrar atendimentos?</DialogTitle>
          </DialogHeader>

          {previaFecharTodos && (
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>
                Seus atendimentos em andamento:{" "}
                <strong className="text-foreground">
                  {previaFecharTodos.meus}
                </strong>
                . Eles passam para Resolvidos e as mensagens continuam salvas.
              </p>

              {previaFecharTodos.deOutros > 0 && (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
                  <p className="font-medium text-foreground">
                    Atenção: há {previaFecharTodos.deOutros} atendimento(s) de
                    outros atendentes em andamento.
                  </p>
                  <ul className="mt-1.5 list-disc pl-5">
                    {previaFecharTodos.atendentes.map((a) => (
                      <li key={a.nome}>
                        {a.nome}: {a.total}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1.5">
                    Eles não serão encerrados, a menos que você escolha
                    incluí-los.
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:flex-wrap">
            <Button variant="outline" onClick={() => setPrevia(null)}>
              Cancelar
            </Button>
            {previaFecharTodos?.deOutros > 0 && (
              <Button variant="destructive" onClick={() => fecharTodos(true)}>
                Encerrar também os de outros (
                {previaFecharTodos.meus + previaFecharTodos.deOutros})
              </Button>
            )}
            <Button
              disabled={!previaFecharTodos?.meus}
              onClick={() => fecharTodos(false)}
            >
              Encerrar só os meus ({previaFecharTodos?.meus ?? 0})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lista.
          Coluna de verdade (flex), não mais uma pilha de absolutos com a
          barra de período flutuando por cima: o recuo da lista era um
          `top-[41px]` fixo, e como os botões da barra quebravam em duas
          linhas ela crescia para 57px e cobria o primeiro atendimento. Em
          fluxo normal a barra ocupa o que precisa e a lista começa embaixo
          dela, qualquer que seja a altura. */}
      <div className="flex min-h-0 flex-1 flex-col border-t">
        {buscando ? (
          <div className="relative min-h-0 flex-1">
            <TicketsList
              searchParam={searchParam}
              showAll={true}
              selectedQueueIds={selectedQueueIds}
              vazio={{
                titulo: "Nenhum resultado",
                texto: "Tente outro nome, número ou trecho da conversa.",
              }}
            />
          </div>
        ) : (
          <>
            {/* Recorte de período: fica só no painel de fechados, que é o que
                acumula. Nas abas de abertos não faz sentido — o que está
                aberto interessa independentemente da idade. */}
            {painel === "fechados" && (
              <div className="flex shrink-0 items-center gap-1.5 border-b bg-background px-2 py-1.5">
                {/* Fita segmentada, como as abas: um trilho cinza com a opção
                    escolhida em pastilha. Antes eram quatro botões soltos, e
                    sem o trilho não se lia que eram alternativas do mesmo
                    ajuste. `whitespace-nowrap` porque "7 dias" quebrando em
                    duas linhas dobrava a altura da barra. */}
                <div className="flex min-w-0 flex-1 gap-0.5 rounded-lg bg-muted p-0.5">
                  {[
                    { v: "7", r: "7 dias" },
                    { v: "30", r: "30 dias" },
                    { v: "90", r: "90 dias" },
                    { v: "todos", r: "Tudo" },
                  ].map((op) => (
                    <button
                      key={op.v}
                      type="button"
                      onClick={() => setPeriodoResolvidos(op.v)}
                      aria-pressed={periodoResolvidos === op.v}
                      className={cn(
                        "grow whitespace-nowrap rounded-md px-1 py-1 text-[11px] font-medium transition-all",
                        periodoResolvidos === op.v
                          ? "bg-background text-foreground shadow-sm ring-1 ring-black/[0.04] dark:ring-white/10"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {op.r}
                    </button>
                  ))}
                </div>

                {/* Agrupar por cliente. Ligado, a aba mostra uma linha por
                    contato — o atendimento mais recente dele. Desligado,
                    mostra chamado por chamado, para quem precisa auditar.
                    Só o ícone: o rótulo "Por cliente" custava 60px da barra
                    para dizer o que o próprio boneco já diz, e era ele que
                    forçava a quebra de linha. */}
                <button
                  type="button"
                  onClick={() => setAgruparEncerrados((v) => !v)}
                  aria-pressed={agruparEncerrados}
                  aria-label="Agrupar por cliente"
                  title={
                    agruparEncerrados
                      ? "Mostrando um por cliente — clique para ver todos os chamados"
                      : "Mostrando todos os chamados — clique para agrupar por cliente"
                  }
                  className={cn(
                    "flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors",
                    agruparEncerrados
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <UserRound className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            <div className="relative min-h-0 flex-1">
            {abas.map((item) => (
            <div
              key={item.value}
              className={cn(
                "absolute inset-0",
                // A barra de período ocupa o topo: sem empurrar a lista, ela
                // fica por cima do primeiro atendimento.
                estado !== item.value && "invisible",
              )}
            >
              {item.chatInterno ? (
                <div className="h-full overflow-y-auto">
                  <ListaDoChatInterno aoContar={setNaoLidasInternas} />
                </div>
              ) : (
              <TicketsList
                status={item.status}
                visao={
                  item.value === "minhas" && verSetor ? "setor" : item.visao
                }
                ultimosDias={
                  item.value === "closed" && periodoResolvidos !== "todos"
                    ? periodoResolvidos
                    : undefined
                }
                // Encerrados: uma linha por cliente. O mesmo contato aparecia
                // uma vez para cada vez que voltou a escrever, e a aba virava
                // uma peneira de nomes repetidos.
                agruparPorContato={item.value === "closed" && agruparEncerrados}
                onlyFavorites={soFavoritas}
                showAll={item.value === "closed" ? true : showAllTickets}
                ordemAZ={ordemAZ}
                tagIds={filtroTags}
                userIds={filtroUsuarios}
                whatsappIds={filtroConexoes}
                dataInicio={filtroDataInicio}
                dataFim={filtroDataFim}
                selectedQueueIds={selectedQueueIds}
                isGroup={item.somenteGrupos ? "true" : "false"}
                vazio={
                  item.somenteGrupos && !mostrarGrupos
                    ? {
                        titulo: "Grupos não estão virando atendimento",
                        texto:
                          "Ligue “Grupos viram atendimento” na conexão para as conversas de grupo aparecerem aqui.",
                      }
                    : item.vazio
                }
                updateCount={
                  item.value === "closed"
                    ? undefined
                    : atualizarContagem(item.value)
                }
              />
              )}
              </div>
            ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TicketsManager;
